import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { LuxuryCard, Indicium } from "@/components/luxury";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { HeartStraightIcon as HeartStraight, LightningIcon as Lightning, ThermometerIcon as Thermometer, BabyIcon as Baby, BuildingsIcon as Buildings, ScissorsIcon as Scissors } from "@phosphor-icons/react";

/**
 * /me/buyer-preferences
 *
 * The buyer-side lifestyle questionnaire that fuels the Smart Matchmaker's
 * `lifestyle_fit` signal. Six dimensions:
 *
 *   • energy            (1-5 slider)
 *   • climate           (cold | temperate | hot | all)
 *   • kid_friendly      (1-5 slider)
 *   • apartment_ok      (1-5 slider — buyer's spatial budget)
 *   • grooming_tolerance(1-5 slider — how much weekly grooming the buyer
 *                       is willing to commit to)
 *
 * Every field is optional. Empty fields are not scored — that lets a
 * buyer express only the preferences they care about without the
 * algorithm punishing dogs on dimensions the buyer didn't specify.
 *
 * Visual lineage matches the rest of the heritage-luxury system:
 * cream surfaces, gold hairlines, ink labels, a single WaxSeal-tone
 * gold accent on the active slider thumb. No emoji.
 */

const CLIMATES = [
  { value: "cold",      label: "Cold-hardy",      hint: "snow, mountain, northern climates" },
  { value: "temperate", label: "Temperate",       hint: "mild four-season climates" },
  { value: "hot",       label: "Heat-tolerant",   hint: "Sun Belt, Florida, desert states" },
  { value: "all",       label: "Any climate",     hint: "I move around / no preference" },
];

const ENERGY_LABELS = ["", "Couch potato", "Calm walker", "Active companion", "Athletic", "Marathon runner"];
const KID_LABELS    = ["", "Adults only",  "Older kids",   "Respectful kids",  "Family friendly", "All ages incl. toddlers"];
const APT_LABELS    = ["", "Acreage / runs", "Big yard",    "Suburban yard",    "Townhouse / condo", "Apartment perfect"];
const GROOM_LABELS  = ["", "Wash and go",   "Monthly bath", "Weekly brushing",  "Frequent grooming", "Daily + pro monthly"];

export default function BuyerPreferences() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    energy: null,
    climate: null,
    kid_friendly: null,
    apartment_ok: null,
    grooming_tolerance: null,
  });

  useEffect(() => {
    api.get("/me/buyer-preferences")
      .then((r) => {
        // Strip server-side metadata fields (`updated_at`, `_id`, etc.)
        // before they leak into local state. Pydantic would silently
        // drop them on the next PUT, but it's cleaner to never store
        // them in the first place.
        const incoming = r.data || {};
        setPrefs((p) => ({
          ...p,
          ...Object.fromEntries(
            Object.entries(incoming).filter(([k]) => k in p)
          ),
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setSlider = (key) => (v) => setPrefs((p) => ({ ...p, [key]: v[0] }));
  const clearKey = (key) => setPrefs((p) => ({ ...p, [key]: null }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/me/buyer-preferences", prefs);
      toast.success("Preferences saved", {
        description: "Your dog matches will reflect this on the next dashboard load.",
      });
      navigate("/dashboard");
    } catch (e) {
      toast.error("Couldn't save preferences", { description: "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="text-center text-[#6B6B6B] py-20">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Helmet>
        <title>Lifestyle preferences · Armstrong K9 Registry</title>
      </Helmet>

      <div className="max-w-3xl mx-auto">
        <header className="mb-10">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-gold-dark mb-2">
            Find My Dog
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight">
            Tell us what you're looking for.
          </h1>
          <p className="text-[#6B6B6B] mt-3 max-w-xl">
            Five quick questions and we'll start surfacing verified kennels
            working the lines that fit your home. Skip any you don't have a
            strong opinion on — partial answers still help. (This matches you
            with a dog to bring home — breeders looking for studs or dams
            should use the{" "}
            <Link to="/breeding" className="underline decoration-gold underline-offset-2 hover:text-ink" data-testid="prefs-to-breeding-link">
              Breeding Matchmaker
            </Link>
            .)
          </p>
        </header>

        <div className="space-y-6" data-testid="buyer-prefs-form">
          <SliderRow
            icon={Lightning}
            title="Energy level"
            hint="How active will the dog's daily life be?"
            value={prefs.energy}
            onChange={setSlider("energy")}
            onClear={() => clearKey("energy")}
            labels={ENERGY_LABELS}
            testid="prefs-energy"
          />

          <ClimateRow
            value={prefs.climate}
            onChange={(v) => setPrefs((p) => ({ ...p, climate: v }))}
          />

          <SliderRow
            icon={Baby}
            title="Kid-friendliness"
            hint="Who lives in the home? Tolerance for chaos."
            value={prefs.kid_friendly}
            onChange={setSlider("kid_friendly")}
            onClear={() => clearKey("kid_friendly")}
            labels={KID_LABELS}
            testid="prefs-kid"
          />

          <SliderRow
            icon={Buildings}
            title="Living space"
            hint="From acreage to studio apartment — what fits your dog?"
            value={prefs.apartment_ok}
            onChange={setSlider("apartment_ok")}
            onClear={() => clearKey("apartment_ok")}
            labels={APT_LABELS}
            testid="prefs-apartment"
          />

          <SliderRow
            icon={Scissors}
            title="Grooming tolerance"
            hint="How much weekly grooming are you willing to commit to?"
            value={prefs.grooming_tolerance}
            onChange={setSlider("grooming_tolerance")}
            onClear={() => clearKey("grooming_tolerance")}
            labels={GROOM_LABELS}
            testid="prefs-grooming"
          />
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-7"
            data-testid="prefs-save-btn"
          >
            {saving ? "Saving…" : "Save preferences"}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="rounded-full h-11 px-6 border-[#E2DAC2]"
            data-testid="prefs-cancel-btn"
          >
            Cancel
          </Button>
          <p className="text-xs text-[#6B6B6B] flex items-center gap-2 ml-auto">
            <HeartStraight size={14} weight="duotone" className="text-gold-dark" />
            Your answers stay private — used only to rank matches.
          </p>
        </div>
      </div>
    </AppShell>
  );
}

function SliderRow({ icon: Icon, title, hint, value, onChange, onClear, labels, testid }) {
  // Slider is uncontrolled-ish — we render `value || 3` as the visual
  // mid-point but treat value=null as "no preference set". The user
  // explicitly clearing returns to that null state and removes the
  // dimension from scoring.
  const active = value !== null && value !== undefined;
  const visual = active ? value : 3;
  return (
    <LuxuryCard className="p-6">
      <div className="flex items-start gap-4">
        <Indicium icon={Icon} tone="gold" size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="font-spectral text-xl tracking-tight">{title}</h3>
            {active && (
              <button
                type="button"
                onClick={onClear}
                className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6B6B6B] hover:text-crimson transition-colors"
                data-testid={`${testid}-clear`}
              >
                Clear
              </button>
            )}
          </div>
          <p className="text-sm text-[#6B6B6B] mt-1">{hint}</p>
          <div className="mt-5">
            <Slider
              value={[visual]}
              onValueChange={onChange}
              min={1}
              max={5}
              step={1}
              data-testid={testid}
              className={active ? "" : "opacity-50"}
            />
            <div className="flex justify-between mt-2 text-[11px] text-[#6B6B6B] font-tabular">
              <span>{labels[1]}</span>
              <span>{labels[5]}</span>
            </div>
            <p
              className={`mt-3 text-sm font-semibold ${active ? "text-gold-dark" : "text-[#6B6B6B]"}`}
              data-testid={`${testid}-readout`}
            >
              {active ? labels[value] : "No preference set — tap to choose"}
            </p>
          </div>
        </div>
      </div>
    </LuxuryCard>
  );
}

function ClimateRow({ value, onChange }) {
  return (
    <LuxuryCard className="p-6">
      <div className="flex items-start gap-4">
        <Indicium icon={Thermometer} tone="gold" size="md" />
        <div className="flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="font-spectral text-xl tracking-tight">Climate</h3>
            {value && (
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6B6B6B] hover:text-crimson transition-colors"
                data-testid="prefs-climate-clear"
              >
                Clear
              </button>
            )}
          </div>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Climate suitability for the breed's coat and build.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            {CLIMATES.map((c) => {
              const selected = value === c.value;
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => onChange(c.value)}
                  data-testid={`prefs-climate-${c.value}`}
                  className={`text-left rounded-xl px-4 py-3 border transition-all ${
                    selected
                      ? "border-gold bg-gold-soft/40 shadow-[inset_0_0_0_1px_rgba(201,162,39,0.45)]"
                      : "border-[#E2DAC2] bg-white hover:border-gold/55"
                  }`}
                >
                  <div className={`font-medium ${selected ? "text-ink" : "text-ink/85"}`}>
                    {c.label}
                  </div>
                  <div className="text-[12px] text-[#6B6B6B] mt-0.5">{c.hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </LuxuryCard>
  );
}
